<template>
  <div id="app">
    <header class="p-4 shadow-md border-t-4 border-black flex flex-wrap text-center justify-center sm:justify-between items-center text-center">
      <h1>Coloring for Colorblindness</h1>
      <a href="https://davidnichols.ai" target="_blank">by David Nichols</a>
    </header>
    <main>
      <h2 class="mt-0">We see colors differently</h2>
      <p>
        About 1 in 20 people are colorblind in some way. This means that one or more of the types of cone cells in their eyes &mdash; the cells we use to detect color &mdash; does not work normally. Because of this, a person who is colorblind sees a reduced color space; two colors which appear distinct to a person with normal color vision may appear to be the same to a person who is colorblind. This phenomenon is not random. Mathematically, there are lines through color space &mdash; called "confusion lines" &mdash; such that every color on a confusion line appears the same to a person who is colorblind as every other color on that same confusion line. 
      </p>
      <p>
        This means that we can, to some degree, simulate colorblindness. By mathematically replicating the confusion line phenomenon, we can replace the colors in a picture so that if two of the original colors are on the same confusion line, they each get replaced by the same new color. If we do this the right way, a person with normal color vision can experience something like colorblindness. 
      </p>
      <p>
        This website is for people with normal or nearly normal color vision. The purpose of this website is to simulate for you what your chosen color palette looks like to viewers who are colorblind. The colors in the leftmost column are the "true" colors; these are displayed in the remaining three columns the way that a person with <b>prot</b>anopia, <b>deut</b>eranopia, or <b>trit</b>anopia would see them, respectively. 
      </p>

      <h2 class="text-center">Color Palette</h2>
      <div class="flex flex-row justify-center items-start pb-2">
        <color-column label="True" :colors="colors" :selectedIndex="selectedIndex" trueColors @select="index => selectColor(index)"/>
        <color-column label="Prot." :colors="protanopiaColors"/>
        <color-column label="Deut." :colors="deuteranopiaColors"/>
        <color-column label="Trit." :colors="tritanopiaColors"/>
      </div>
      
      <div class="flex flex-row justify-center items-stretch my-8">
        <color-picker v-model="selectedColor" :disableAlpha="true"></color-picker>
        <div class="flex flex-col items-center justify-around w-16 ml-8">
          <button @click.prevent="removeColor"
                  class="flex justify-center items-center w-16 h-16 rounded-full border border-grey-darker text-grey-darker text-xl hover:shadow-md" aria-label="Delete color." :disabled="selectedIndex === -1">
            <i class="fa fa-trash"></i>
          </button>
          <button @click.prevent="addColor" 
                  class="w-16 h-16 rounded-full border border-solid border-grey-darker text-grey-darker text-xl flex justify-center items-center cursor-pointer hover:border-solid hover:font-bold hover:shadow-md"
                  aria-label="Add color.">
            <i class="fa fa-plus"></i>
          </button>
          <button @click.prevent="resetColors"
                  class="flex justify-center items-center w-16 h-16 rounded-full border border-grey-darker text-grey-darker text-xl hover:shadow-md" aria-label="Reset colors to defaults.">
            <i class="fa fa-refresh"></i>
          </button>
        </div>
      </div>

      <p class="bg-grey-light p-3">
        Click on one of the true colors to view or change it using the color picker. Click the <i class="fa fa-trash" aria-label="Icon for deletion."></i> button to remove a selected color from the color palette. Click the <i class="fa fa-plus" aria-label="Icon for new addition."></i> button to add a new color to the color palette. Click the <i class="fa fa-refresh" aria-label="Icon for resetting to default."></i> button to reset all of the colors to my defaults. Your color palette is encoded in the URL for this page while you work, so you can save or share a color palette by saving the URL when you are finished. For example, here are links to <a href="#%23648FFF-%23785EF0-%23DC267F-%23FE6100-%23FFB000" @click.prevent="pushColors(['#648FFF', '#785EF0', '#DC267F', '#FE6100', '#FFB000'])">the IBM design library "color blind safe" color palette</a> and to <a href="#%23000000-%23E69F00-%2356B4E9-%23009E73-%23F0E442-%230072B2-%23D55E00-%23CC79A7" @click.prevent="pushColors(['#000000', '#E69F00', '#56B4E9', '#009E73', '#F0E442', '#0072B2', '#D55E00', '#CC79A7'])">the "colorblind barrier-free" color palette</a> from an <a href="https://jfly.uni-koeln.de/color/" rel="external">Okabe and Ito 2002 article on J*FLY</a>.
      </p>

      <h2>There's more</h2>
      <p>
        There are other types of colorblindness and color vision anomaly than the three types mentioned above. I recommend looking at <a href="https://en.wikipedia.org/wiki/Color_blindness#Types" rel="external">the Wikipedia entry on the subject</a> for more information. The most common type of color vision anomaly is deuteranomaly, which involves reduced sensitivity &mdash; but not blindness &mdash; to the green region of the spectrum. Deuteranomaly affects 1 in every 16 or 17 men and 1 in every 250 women. You can see a simulation of deuteranomalous vision below. 
      </p>

      <div class="flex flex-row justify-center items-start pb-2 my-8">
        <color-column label="True" :colors="colors" normal/>
        <color-column label="Dtnm." :colors="deuteranomalyColors"/>
      </div>

      <p>
        The colorblindness simulations you see in the tool above are produced using state-of-the-art calculations. But while these calculations are widely used and broadly useful, the confusion lines used are imperfect and more study is required &mdash; especially regarding the rarer types of colorblindness &mdash; in order to say with certainty how exactly these simulations mirror the color vision of each person with colorblindness. 
      </p>

      <h2>Red and green</h2>
      <p>
        The three types of cone cell in your eye are each responsible for detecting a different primary color: red, green, and blue. Because anomalies in these cells interfere with a person's ability to see these colors normally, it is a bad idea to use a color palette of red, green, and blue. In particular, you should avoid the common practice of using red and green as contrasting colors. See why for yourself. 
      </p>

      <div class="flex flex-row justify-center items-start pb-2 my-8">
        <color-column label="True" :colors="['#f00', '#0f0']" normal/>
        <color-column label="Prot." :colors="['#8f7e1e', '#f8dc00']"/>
        <color-column label="Deut." :colors="['#a17800', '#ffd599']"/>
      </div>

      <p>
        Most people who are colorblind have some form of red-green colorblindness, such as deuteranomaly, deuteranopia, or protanopia. To a person with one of these forms of colorblindness, it can difficult or impossible to distinguish whether a given color is red or green, and shades of red and green may have very low contrast. Instead of using a color palette of red and green, consider using a similar <i>magenta</i> and green  color palette instead. 
      </p>

      <div class="flex flex-row justify-center items-start pb-2 my-8">
        <color-column label="True" :colors="['#d81b60', '#0f0']" normal/>
        <color-column label="Prot." :colors="['#525975', '#f8dc00']"/>
        <color-column label="Deut." :colors="['#68564e', '#ffd599']"/>
      </div>

      <p>
        Another common practice is to use red to highlight text. Again, this will not work well for some of your readers. People who are red-green colorblind will see a lower contrast between red text and the neutral colors which surround it. 
      </p>

      <h2>Accessible palettes</h2>
      <p>
        So what colors <i>should</i> you use? The colorpicker tool above is intended to give the freedom to choose your own colors while making sure that your color palette is accessible. But to get you started, here are some ideas. Here are 8 pairs of contrasting colors which maintain their contrast for people who are colorblind. Click on any of them to load it into the color palette selection tool above. 
      </p>

      <div class="flex flex-wrap justify-around items-center my-8">
        <div v-for="pair in contrastingPairs" @click="pushColors(pair)" class="flex justify-start items-center p-2 mb-4 w-64 shadow-md cursor-pointer hover:shadow-lg" style="border-top: 1px solid rgba(0,0,0,0.05);">
          <div class="flex flex-col w-16 h-32">
            <div v-for="color in pair" v-bind:key="color" 
                :style="{'background': color}" class="w-16 h-16" >
            </div>
          </div>
          <div class="flex flex-col h-32">
            <div v-for="color in pair" v-bind:key="color" class="h-16 pl-2 flex flex-col justify-center items-start">
              <span><b>HEX</b> {{color}}</span>
              <span>
                <b>R</b> {{parseInt(color.substring(1,3), 16)}}&ensp;
                <b>G</b> {{parseInt(color.substring(3,5), 16)}}&ensp;
                <b>B</b> {{parseInt(color.substring(5,7), 16)}}
              </span>
            </div>
          </div>
        </div>
      </div>

      <p>
        A number of color palettes have been developed with the intention of being accessible to people who are colorblind. Three of them appear below, from the <a href="https://web.archive.org/web/20180503023309/https://www.ibm.com/design/language/resources/color-library" rel="external">IBM Design Library</a>, <a href="https://jfly.uni-koeln.de/color/" rel="external">Okabe & Ito</a>, and <a href="https://sronpersonalpages.nl/~pault/" rel="external">Paul Tol</a> respectively. Click on any of them to load it into the color palette selection tool above.
      </p>

      <div class="flex flex-col items-center">
        <div v-for="palette in palettes" v-bind:key="palette.name" class="p-2 my-4 shadow-md hover:shadow-lg cursor-pointer inline-block" style="border-top: 1px solid rgba(0,0,0,0.05);" @click="pushColors(palette.colors)">
          <h3 class="text-center">{{palette.name}}</h3>
          <div class="flex flex-wrap justify-center">
            <div v-for="color in palette.colors" v-bind:key="color" class="w-16 h-16" :style="{'background': color}"></div>
          </div>
        </div>
      </div>

      <h2>Beyond colors</h2>
      <p>
        It is a good practice to, where possible, avoid conveying information purely through color. You should always consider adding other ways to convey the same information besides just color. For example, you might use text, symbols, or patterns. In a bar chart with bars of different colors, consider also giving the bars different patterns; perhaps bars of one color could also be dotted in black, while bars of another color could be overlaid with a crosshatch pattern. If you are using color to emphasize text, consider also using italics or boldface or some other form of emphasis as well. 
      </p>
    </main>
    <footer class="h-12 text-sm flex justify-center items-center">
      © David Nichols
    </footer>
  </div>
</template>

<script>
import blinder from 'color-blind'
window.blinder = blinder
import { Chrome } from 'vue-color'
import colorColumn from './components/ColorColumn.vue'
export default {
  components: {
    colorColumn, 
    colorPicker: Chrome
  },
  name: 'app',
  data() {
    return {
      colors: ['#D81B60','#1E88E5', '#FFC107', '#004D40'], 
      selectedIndex: -1,
      selectedColor: '',
      showColorPicker: true, 
      contrastingPairs: [
        ['#FFC20A', '#0C7BDC'], // amber and blue 
        ['#994F00', '#006CD1'], // brown and blue 
        ['#E1BE6A', '#40B0A6'], // tan and teal 
        ['#E66100', '#5D3A9B'], // orange and purple 
        ['#1AFF1A', '#4B0092'], // green and purple 
        ['#FEFE62', '#D35FB7'], // yellow and pink 
        ['#005AB5', '#DC3220'], // blue and red 
        ['#1A85FF', '#D41159'] // blue and magenta
      ], 
      palettes: [
        {
          'name': 'IBM',
          'colors': ['#648FFF', '#785EF0', '#DC267F', '#FE6100', '#FFB000']
        },
        {
          'name': 'Okabe & Ito',
          'colors': ['#000000', '#E69F00', '#56B4E9', '#009E73', '#F0E442', '#0072B2', '#D55E00', '#CC79A7']
        },
        {
          'name': 'Tol', 
          'colors': ['#332288', '#117733', '#44AA99', '#88CCEE', '#DDCC77', '#CC6677', '#AA4499', '#882255']
        }
      ]
      /*
        Viridis: 
        #%23440154-%23481567-%23482677-%23453781-%23404788-%2339568C-%2333638D-%232D708E-%23287D8E-%23238A8D-%231F968B-%2320A387-%2329AF7F-%233CBB75-%2355C667-%2373D055-%2395D840-%23B8DE29-%23DCE319-%23FDE725

        ["#440154", "#481567", "#482677", "#453781", "#404788", "#39568C", "#33638D", "#2D708E", "#287D8E", "#238A8D", "#1F968B", "#20A387", "#29AF7F", "#3CBB75", "#55C667", "#73D055", "#95D840", "#B8DE29", "#DCE319", "#FDE725"]

        Paul Tol: 
        https://personal.sron.nl/~pault/

        #%23332288-%23117733-%2344AA99-%2388CCEE-%23DDCC77-%23CC6677-%23AA4499-%23882255

        ["#332288", "#117733", "#44AA99", "#88CCEE", "#DDCC77", "#CC6677", "#AA4499", "#882255"]
      */
    }
  }, 
  methods: {
    addColor() {
      this.colors = [ ...this.colors, '#' + Math.floor(Math.random()*16777215).toString(16) ]
      /* 
        random HEX color string code snippet courtesy of 
        https://www.paulirish.com/2009/random-hex-color-code-snippets/
      */
      this.selectedIndex = this.colors.length - 1
      this.selectedColor = this.colors[this.selectedIndex]
    },
    removeColor() {
      this.colors.splice(this.selectedIndex,1)
    },
    resetColors() {
      this.colors = ['#D81B60','#1E88E5', '#FFC107', '#004D40']
      this.selectedIndex = -1
      this.selectedColor = ''
    },
    selectColor(index) {
      const notDeselect = this.selectedIndex !== index
      if (notDeselect) {
        this.selectedIndex = index
        this.selectedColor = this.colors[index]
      } else {
        this.selectedIndex = -1
        this.selectedColor = ''
      }
    },
    hashFromColors() {
      location.hash = this.colors.reduce((t,e) => t + '-' + encodeURIComponent(e), '').slice(1)
    }, 
    colorsFromHash() {
      if (location.hash.slice(1)) {
        this.colors = location.hash.slice(1).split('-').map(e => decodeURIComponent(e))
      }
    }, 
    colorsFromGivenHash(hash) {
      this.colors = hash.split('-').map(e => decodeURIComponent(e))
    }, 
    pushColors(array) {
      this.colors = array
      window.scrollTo(0,0)
    }
  },
  computed: {
    protanopiaColors() {
      return this.colors.map(c => blinder.protanopia(c))
    }, 
    deuteranopiaColors() {
      return this.colors.map(c => blinder.deuteranopia(c))
    },
    tritanopiaColors() {
      return this.colors.map(c => blinder.tritanopia(c))
    }, 
    deuteranomalyColors() {
      return this.colors.map(c => blinder.deuteranomaly(c))
    }
  }, 
  mounted() {
    location.hash.slice(1) ? this.colorsFromHash() : this.hashFromColors()
  }, 
  watch: {
    colors() {
      this.hashFromColors()
    }, 
    selectedColor(val) {
      let newColors = this.colors.map((e,i) => {
        return val.hex && i === this.selectedIndex ? val.hex : e
      })
      this.colors = newColors
    }
  }
}
</script>

<style>
@tailwind preflight; 

* {
  box-sizing: border-box;
  font-family: inherit;
}

html {
  font-family: 'Nunito Sans', Helvetica, Arial, sans-serif;
  font-size: 16px;
  @apply leading-normal;
}

button[disabled] {
  @apply border-grey-light text-grey-light cursor-not-allowed;
}

button[disabled]:hover {
  box-shadow: none;
}

header {
  min-height: 4.5rem;
}

header a {
  @apply text-black no-underline border-b border-black leading-tight;
}

header h1 {
  line-height: 1.1;
}

main {
  margin: 2em auto; 
  width: 92%;
  max-width: 1100px;
}

footer {
  background-image: linear-gradient(to right, #e3ecec, #efe9e9); 
  /* IBM cool gray 1 -> IBM warm gray 1 */
  margin-top: 4rem;
}

p {
  @apply mb-2;
}

h2 {
  @apply my-4;
}

.swatch {
  position: relative;
  cursor: pointer;
}

.swatch:hover {
  transform: scale(1.09);
  @apply z-10 shadow-md;
}

.selected, .selected:hover {
  border: 4px solid #fdfdfd;
  transform: scale(1.1);
  @apply z-20 shadow-md;
}

.selected::before {
  content: ''; 
  position: absolute; 
  top: -4px; right: -4px; bottom: -4px; left: -4px; 
  border: 1px solid rgba(0,0,0,0.07);
}

.vc-chrome {
  font-family: inherit !important;
  box-shadow: 0 4px 8px 0 rgba(0,0,0,0.16), 0 2px 4px 0 rgba(0,0,0,0.12) !important;
  /*box-shadow: 0 0 2px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.3) !important;*/
}

.vc-chrome-fields .vc-input__input {
  font-size: 0.875rem !important;
}

.vc-chrome-fields .vc-input__label {
  font-size: 0.875rem !important;
  color: config(colors['grey-darkest']) !important;
}

@tailwind utilities; 
</style>



// WEBPACK FOOTER //
// src/App.vue?9294196c